import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
    loginRequest,
    resetLoginRequest,
} from "../../redux/modules/auth/authSlice";
import { toast } from "react-toastify";
import {
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Radio,
    RadioGroup,
    Typography,
    CircularProgress,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

const PageContainer = styled("div")(({ theme }) => ({
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    "@media (max-width:768px)": {
        padding: theme.spacing(2),
    },
    "@media (max-width:400px)": {
        padding: theme.spacing(1),
    },
}));

const LoginCard = styled("div")(({ theme }) => ({
    display: "flex",
    width: "100%",
    maxWidth: 1100,
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    backgroundColor: theme.palette.background.paper,
    "@media (max-width:960px)": {
        flexDirection: "column",
        maxWidth: 480,
    },
    "@media (max-width:400px)": {
        borderRadius: 16,
        maxWidth: "100%",
    },
}));

const BrandSide = styled("div")(({ theme }) => ({
    flex: "1 1 50%",
    background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6),
    "@media (max-width:960px)": {
        padding: theme.spacing(4),
        minHeight: "auto",
    },
}));

const FormSide = styled("div")(({ theme }) => ({
    flex: "1 1 50%",
    padding: theme.spacing(6),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    "@media (max-width:960px)": {
        padding: theme.spacing(4),
    },
    "@media (max-width:400px)": {
        padding: theme.spacing(2.5),
    },
}));

const FormLabel = styled("div")(({ theme }) => ({
    fontSize: "0.875rem",
    fontWeight: 500,
    marginBottom: 8,
    color: theme.palette.text.primary,
}));

const CustomLink = styled(Link)(({ theme }) => ({
    display: "inline-block",
    textAlign: "end",
    fontSize: "0.875rem",
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "none",
    fontWeight: 500,
    transition: "color 150ms ease",
    "&:hover": {
        color: theme.palette.primary.dark,
    },
}));

const SubmitButton = styled("button")(({ theme }) => ({
    width: "100%",
    padding: "14px 24px",
    border: "none",
    borderRadius: 10,
    background: theme.palette.accent?.main || theme.palette.primary.main,
    color: "#fff",
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: "normal",
    cursor: "pointer",
    transition: "all 150ms ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    "&:hover": {
        background: theme.palette.accent?.dark || theme.palette.primary.dark,
        transform: "translateY(-1px)",
    },
    "&:disabled": {
        opacity: 0.7,
        cursor: "not-allowed",
        transform: "none",
    },
}));

const RoleCard = styled(FormControlLabel)(({ theme, selected }) => ({
    position: "relative",
    flex: 1,
    margin: 0,
    padding: "16px 20px",
    borderRadius: 12,
    backgroundColor: selected
        ? `rgba(105, 203, 249, 0.1)`
        : theme.palette.background.subtle || theme.palette.grey[50],
    border: "2px solid",
    borderColor: selected ? theme.palette.primary.main : "transparent",
    cursor: "pointer",
    transition: "all 150ms ease",
    // Minimum touch target size (48px per WCAG)
    minHeight: 48,
    "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: `rgba(105, 203, 249, 0.05)`,
    },
    "& .MuiRadio-root": {
        position: "absolute",
        top: 8,
        right: 8,
        padding: 0,
    },
    "& .MuiTypography-root": {
        fontWeight: 500,
        fontSize: "0.9375rem",
    },
}));

const StyledInput = styled(OutlinedInput)(({ theme }) => ({
    borderRadius: 10,
    backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
    transition: "all 150ms ease",
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
        transition: "all 150ms ease",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.divider,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.error.main,
    },
    "& .MuiInputBase-input": {
        padding: "14px 16px",
        fontSize: "0.9375rem",
    },
}));

const schema = yup.object().shape({
    email: yup.string().required("Email or username is required"),
    password: yup.string().required("Password is required"),
    role: yup.string().required("Please select Login user type"),
});

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const { loading, isLoggedIn, res, userData } = useSelector(
        (state) => state.auth
    );
    const [showPassword, setShowPassword] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
        trigger,
        setValue,
        watch,
    } = useForm({
        resolver: yupResolver(schema),
    });

    const selectedRole = watch("role");

    useEffect(() => {
        if (res) {
            if (isLoggedIn) {
                // External referrers have a special portal at /external
                if (userData.role === 'externalReferrer') {
                    navigate('/external');
                } else {
                    navigate(`/${userData.role}`);
                }
            }
            toast(res?.message);
            dispatch(resetLoginRequest());
        }
    }, [res, userData, isLoggedIn, navigate, dispatch]);

    const onSubmit = (data) => {
        dispatch(loginRequest({
            ...data,
            email: data.email.trim().toLowerCase(),
            password: (data.password || '').replace(/\s+/g, ''),
        }));
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    return (
        <PageContainer>
            <LoginCard>
                {/* Brand Side - Hidden on mobile */}
                <BrandSide sx={{ display: { xs: "none", md: "flex" } }}>
                    <Box sx={{ textAlign: "center", mb: 4 }}>
                        <Typography
                            variant="h2"
                            sx={{
                                color: theme.palette.primary.dark,
                                fontWeight: 600,
                                mb: 1,
                            }}
                        >
                            Hello,
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                color: theme.palette.primary.dark,
                                fontWeight: 500,
                            }}
                        >
                            Welcome to Voxxil HIS
                        </Typography>
                    </Box>
                    <Box sx={{ maxWidth: 400 }}>
                        <img
                            alt="Login"
                            src={new URL("../../assets/images/main-logo.png", import.meta.url).href}
                            style={{ width: "100%", height: "auto" }}
                        />
                    </Box>
                </BrandSide>

                {/* Form Side */}
                <FormSide>
                    <Box sx={{ maxWidth: { xs: "100%", sm: 400 }, mx: "auto", width: "100%" }}>
                        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 600,
                                    color: "text.primary",
                                    mb: 1,
                                    fontSize: { xs: "1.5rem", sm: "2.125rem" },
                                }}
                            >
                                Sign In
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                            >
                                Please enter your credentials to continue.
                            </Typography>
                        </Box>

                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/* Role Selection */}
                            <Box sx={{ mb: 3 }}>
                                <FormLabel>I am a...</FormLabel>
                                <FormControl
                                    error={Boolean(errors.role)}
                                    sx={{ width: "100%" }}
                                >
                                    <RadioGroup
                                        row
                                        aria-labelledby="login-role-selection"
                                        name="role-selection"
                                        sx={{
                                            gap: 2,
                                            flexWrap: "nowrap",
                                            "@media (max-width:425px)": {
                                                flexDirection: "column",
                                            },
                                        }}
                                    >
                                        <RoleCard
                                            selected={selectedRole === "staff"}
                                            value="staff"
                                            control={
                                                <Radio
                                                    checked={selectedRole === "staff"}
                                                    onChange={() => {
                                                        setValue("role", "staff");
                                                        trigger("role");
                                                    }}
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <LocalHospitalIcon
                                                        sx={{
                                                            color: selectedRole === "staff"
                                                                ? "primary.main"
                                                                : "text.secondary"
                                                        }}
                                                    />
                                                    Staff
                                                </Box>
                                            }
                                        />
                                        <RoleCard
                                            selected={selectedRole === "patient"}
                                            value="patient"
                                            control={
                                                <Radio
                                                    checked={selectedRole === "patient"}
                                                    onChange={() => {
                                                        setValue("role", "patient");
                                                        trigger("role");
                                                    }}
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <PersonIcon
                                                        sx={{
                                                            color: selectedRole === "patient"
                                                                ? "primary.main"
                                                                : "text.secondary"
                                                        }}
                                                    />
                                                    Patient
                                                </Box>
                                            }
                                        />
                                    </RadioGroup>
                                    {errors?.role?.message && (
                                        <FormHelperText sx={{ mt: 1 }}>
                                            {errors?.role?.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Box>

                            {/* Email/Username Field */}
                            <Box sx={{ mb: 3 }}>
                                <FormLabel>Email or Username</FormLabel>
                                <FormControl
                                    sx={{ width: "100%" }}
                                    error={Boolean(errors.email)}
                                >
                                    <StyledInput
                                        {...register("email")}
                                        placeholder="Enter your email or username"
                                        fullWidth
                                    />
                                    {errors?.email?.message && (
                                        <FormHelperText>
                                            {errors?.email?.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Box>

                            {/* Password Field */}
                            <Box sx={{ mb: 2 }}>
                                <FormLabel>Password</FormLabel>
                                <FormControl
                                    sx={{ width: "100%" }}
                                    error={Boolean(errors.password)}
                                >
                                    <StyledInput
                                        {...register("password")}
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter your password"
                                        fullWidth
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    type="button"
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    edge="end"
                                                    sx={{ mr: 0.5 }}
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff fontSize="small" />
                                                    ) : (
                                                        <Visibility fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                    {errors?.password?.message && (
                                        <FormHelperText>
                                            {errors?.password?.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Box>

                            {/* Forgot Password Link */}
                            <Box sx={{ textAlign: "right", mb: 3 }}>
                                <CustomLink to="/forgot-password">
                                    Forgot Password?
                                </CustomLink>
                            </Box>

                            {/* Submit Button */}
                            <SubmitButton type="submit" disabled={Boolean(loading)}>
                                {loading && <CircularProgress size={18} color="inherit" />}
                                Sign In
                            </SubmitButton>
                        </form>
                    </Box>
                </FormSide>
            </LoginCard>
        </PageContainer>
    );
};

export default Login;
